// frontend/src/pages/StocksBodegaPage.jsx
import React, { useState, useEffect } from 'react';
import bodegaService from '../services/bodegaService';
import Loading from '../components/Loading';
import './StocksBodegaPage.css';

const StocksBodegaPage = () => {
  const [stocks, setStocks] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterBodega, setFilterBodega] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [stocksRes, bodegasRes] = await Promise.all([
        bodegaService.getStocks(),
        bodegaService.getBodegas()
      ]);
      // La API devuelve un objeto paginado con la estructura: { count, next, previous, results }
      setStocks(stocksRes.data.results || stocksRes.data);
      setBodegas(bodegasRes.data.results || bodegasRes.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const stocksFiltrados = stocks.filter(stock => {
    const matchBodega = !filterBodega || stock.bodega === parseInt(filterBodega);
    const matchSearch = !searchTerm ||
      stock.recurso_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      stock.recurso_codigo?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = !filterEstado ||
      (filterEstado === 'requiere_reposicion' && stock.requiere_reposicion) ||
      (filterEstado === 'stock_ok' && !stock.requiere_reposicion);
    return matchBodega && matchSearch && matchEstado;
  });

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="stocks-page">
      <div className="page-header">
        <h1>📊 Control de Stock</h1>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="🔍 Buscar recurso..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={filterBodega}
          onChange={(e) => setFilterBodega(e.target.value)}
          className="filter-select"
        >
          <option value="">Todas las bodegas</option>
          {bodegas.map(bod => (
            <option key={bod.id} value={bod.id}>{bod.nombre}</option>
          ))}
        </select>
        <select
          value={filterEstado}
          onChange={(e) => setFilterEstado(e.target.value)}
          className="filter-select"
        >
          <option value="">Todos los estados</option>
          <option value="stock_ok">✅ Stock OK</option>
          <option value="requiere_reposicion">⚠️ Requiere reposición</option>
        </select>
      </div>

      <div className="stocks-table-container">
        <table className="stocks-table">
          <thead>
            <tr>
              <th>Bodega</th>
              <th>Código</th>
              <th>Recurso</th>
              <th>Unidad</th>
              <th>Stock Actual</th>
              <th>Solicitado</th>
              <th>En Tránsito</th>
              <th>Stock Proyectado</th>
              <th>Stock Mínimo</th>
              <th>PPP</th>
              <th>Stock Valorizado</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {stocksFiltrados.length === 0 ? (
              <tr>
                <td colSpan="12" className="empty-row">No hay stocks registrados</td>
              </tr>
            ) : (
              stocksFiltrados.map((stock) => {
                const ppp = parseFloat(stock.precio_costo_unitario || 0);
                const cantidadActual = parseFloat(stock.cantidad_actual || 0);
                const cantidadSolicitada = parseFloat(stock.cantidad_solicitada || 0);
                const cantidadEnTransito = parseFloat(stock.cantidad_en_transito || 0);
                const stockProyectado = parseFloat(stock.stock_proyectado || 0);
                const stockValorizado = cantidadActual * ppp;

                return (
                  <tr key={stock.id} className={stock.requiere_reposicion ? 'row-alert' : ''}>
                    <td><strong>{stock.bodega_nombre}</strong></td>
                    <td>{stock.recurso_codigo}</td>
                    <td>{stock.recurso_nombre}</td>
                    <td>{stock.recurso_unidad_medida}</td>
                    <td className={stock.requiere_reposicion ? 'stock-bajo' : 'stock-ok'}>
                      {cantidadActual.toFixed(2)}
                    </td>
                    <td style={{ color: cantidadSolicitada > 0 ? '#f59e0b' : '#6b7280' }}>
                      {cantidadSolicitada > 0 ? (
                        <strong>{cantidadSolicitada.toFixed(2)}</strong>
                      ) : (
                        cantidadSolicitada.toFixed(2)
                      )}
                    </td>
                    <td style={{ color: cantidadEnTransito > 0 ? '#3b82f6' : '#6b7280' }}>
                      {cantidadEnTransito > 0 ? (
                        <strong>{cantidadEnTransito.toFixed(2)}</strong>
                      ) : (
                        cantidadEnTransito.toFixed(2)
                      )}
                    </td>
                    <td style={{
                      color: stockProyectado < parseFloat(stock.recurso_stock_minimo || 0) ? '#ef4444' : '#059669',
                      fontWeight: 'bold'
                    }}>
                      {stockProyectado.toFixed(2)}
                    </td>
                    <td>{parseFloat(stock.recurso_stock_minimo || 0).toFixed(2)}</td>
                    <td>${ppp.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                    <td><strong>${stockValorizado.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong></td>
                    <td>
                      {stock.requiere_reposicion ? (
                        <span className="badge-alert">⚠️ Requiere reposición</span>
                      ) : (
                        <span className="badge-ok">✅ Stock OK</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StocksBodegaPage;
